import type { AnalyticsApi, AnalyticsEvent } from '@backstage/core-plugin-api';

type Gtag = (command: 'set', params: Record<string, string>) => void;

declare global {
  interface Window {
    gtag?: Gtag;
  }
}

/** Drops the query string, e.g. `/search?query=foo#x` → `/search#x`. */
function stripQueryString(pathOrUrl: string): string {
  return pathOrUrl.replace(/\?[^#]*/, '');
}

/**
 * Decorates an {@link AnalyticsApi} for GA4.
 *
 * 1. **Real page views.** `@backstage-community/plugin-analytics-module-ga4`
 *    only emits `page_view` for `navigate` events whose category
 *    (`context.extensionId || context.extension || 'App'`) starts with `app`.
 *    The new frontend system's `RouteTracker` (`@backstage/plugin-app`) sets
 *    `extensionId` to the routed extension (e.g. `page:catalog`), so every
 *    navigation would otherwise be sent as a custom `navigate` event. For
 *    `navigate` events we forward a copy with `extensionId: 'App'` and keep
 *    the original in `routeExtensionId` (allow-listed as
 *    `c_routeExtensionId`).
 * 2. **No query text in URLs.** The `/search` page keeps the search term in
 *    the URL (`?query=…`), and gtag attaches the current URL to every event as
 *    `page_location`. Before each event we set `page_location` to the URL
 *    without its query string, and strip it from `navigate` subjects too.
 *
 * Other events pass through unchanged. The input event is never mutated.
 */
export class MakeAnalyticsApi implements AnalyticsApi {
  constructor(private readonly inner: AnalyticsApi) {}

  captureEvent(event: AnalyticsEvent): void {
    this.setSanitizedPageLocation();

    if (event.action !== 'navigate') {
      this.inner.captureEvent(event);
      return;
    }

    const { extensionId } = event.context;
    const isAppLike =
      typeof extensionId === 'string' &&
      extensionId.toLocaleLowerCase('en-US').startsWith('app');
    const routeExtensionId =
      extensionId && !isAppLike ? extensionId : undefined;

    this.inner.captureEvent({
      ...event,
      subject: stripQueryString(event.subject),
      context: {
        ...event.context,
        extensionId: 'App',
        ...(routeExtensionId !== undefined ? { routeExtensionId } : {}),
      },
    });
  }

  private setSanitizedPageLocation() {
    if (typeof window === 'undefined') return;
    // gtag is installed on window by react-ga4 when GA4 initializes.
    const { gtag } = window;
    if (typeof gtag !== 'function') return;
    const { origin, pathname, hash } = window.location;
    gtag('set', { page_location: `${origin}${pathname}${hash}` });
  }
}
